/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';
import classnames from 'classnames';
import Papa from 'papaparse';

import { IBulkOrderData } from './bulk-order.data';
import { IBulkOrderProps } from './bulk-order.props.autogenerated';
import { addCartLinesAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/CartsDataActions.g';
import { getCatalogId } from '@msdyn365-commerce/core';
import { CartLine } from '@msdyn365-commerce/retail-proxy';
import { Modal, ModalBody, ModalHeader, ModalFooter } from '@msdyn365-commerce-modules/utilities';
import { productErrorHandling } from '../../utilities/add-to-cart-error-handling';

import { clsHelper } from '../../utilities/class-name-helper';

export interface IBulkOrderViewProps extends IBulkOrderProps<IBulkOrderData> { }

//==========================================================
// CLASS NAME UTILITY
//==========================================================
const BASE_CLASS = 'bulk-order';
const cls = (fragment?: string) => clsHelper(BASE_CLASS, fragment);
//==========================================================

/**
 *
 * BulkOrder component
 * @extends {React.PureComponent<IBulkOrderProps<IBulkOrderData>>}
 */

interface CSVRow {
    productId: string,
    quantity: string
}
interface IState {
    cartErrorMsg: string;
    itemsUploaded: string;
    displayError: boolean;
    errorList: { productId: string, errorMessage: string }[]
    importedItems: { productId: string, quantity: number }[]
}

class BulkOrder extends React.PureComponent<IBulkOrderProps<IBulkOrderData>, IState> {
    public state: IState = {
        cartErrorMsg: '',
        itemsUploaded: '',
        displayError: false,
        errorList: [],
        importedItems: []
    };

    private handleConfirm = () => {
        this.setState({ displayError: false });
        this.addToCart();

    };
    private handleDecline = () => {
        this.setState({ itemsUploaded: "Order canceled." });
        this.setState({ displayError: false });
        return;
    };
    public render(): JSX.Element | null {
        return (
            <div className={BASE_CLASS}>
                {this.uploadCSV()}
                {this.state.cartErrorMsg && <div className={classnames(cls('alert'), 'error')}>{this.state.cartErrorMsg}</div>}
                {this.state.itemsUploaded && <div className={classnames(cls('alert'), 'success')}>{this.state.itemsUploaded}</div>}
                {this.state.displayError && this.displayErrorItem(this.state.errorList)}
            </div>
        );
    }
    public uploadCSV(): JSX.Element | null {
        return (
            <input type="file" accept=".csv" onInput={this.renderCSV} className={cls('input')} />
        );
    }
    private renderCSV = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            file.text().then(csvText => {
                this.usePapaParse(csvText);
            });
        }
    };
    private usePapaParse = async (csvText: string) => {

        //load parsed row as object
        const parsedData = Papa.parse<CSVRow>(csvText, {
            header: true,
            skipEmptyLines: true
        });

        //Check for cart avilability to load items
        const cart = this.props.data.cart.result;
        if (!cart) {
            this.setState({ cartErrorMsg: 'Unable to load cart.' });
            return;
        }

        const allItemIds: { productId: string, quantity: number }[] = [];
        //Used for errored productList utility function
        const itemIds: string[] = [];
        parsedData.data.forEach(row => {
            allItemIds.push({ productId: row.productId, quantity: Number(row.quantity) });
            itemIds.push(row.productId);
        });
        this.setState({ importedItems: allItemIds });

        const errorList = await productErrorHandling(itemIds,this.props.context);

        if (errorList.length > 0) {
            this.setState({ errorList: errorList });
            this.setState({ displayError: true });
        }
        else
        {
            this.addToCart();
        }
    };
    private addToCart() {
        const cart = this.props.data.cart.result;
        const cartLines: CartLine[] = [];
        const catalog = getCatalogId(this.props.context.request);
        const filteredItems = this.state.importedItems.filter(item =>
            !this.state.errorList.some(error => error.productId === item.productId)
        );
        filteredItems.forEach(item => {
            cartLines.push({
                CatalogId: catalog,
                ItemId: item.productId,
                Quantity: item.quantity
            });

        });
        if (cart) {
            addCartLinesAsync(
                { callerContext: this.props.context.actionContext },
                cart.cart.Id,
                cartLines
            ).then(() => {
                this.setState({ itemsUploaded: "Items have successfully been added." });
                cart?.refreshCart({});
            });
        }

    }
    private displayErrorItem(errorList: { productId: string, errorMessage: string }[]): JSX.Element | null {
        return (
            <Modal isOpen={this.state.displayError} className={cls('modal')}>
                <ModalHeader>Errors</ModalHeader>
                <ModalBody>
                    <div className={cls('modal-content')}>
                        <h3 className={cls('modal-heading')}>The following items are incorrect.</h3>
                        <p className={cls('modal-text')}>Would you like to continue the order with the following items being removed?</p>
                        <ul className={cls('modal-items')}>
                            {errorList.map((item, index) => (
                                <li key={index} className={cls('modal-item')}>
                                    <div className={cls('modal-item-product')}>
                                        <span className={cls('modal-item-label')}>Product ID:</span>
                                        <span className={cls('modal-item-value')}>{item.productId}</span>
                                    </div>
                                    <div className={cls('modal-item-error')}>
                                        <span className={cls('modal-item-label')}>Error:</span>
                                        <span className={cls('modal-item-value')}>{item.errorMessage}</span>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>
                </ModalBody>
                <ModalFooter>
                    <button className={classnames(cls('modal-button'), 'confirm')} onClick={this.handleConfirm}>Proceed</button>
                    <button className={classnames(cls('modal-button'), 'cancel')} onClick={this.handleDecline}>Cancel</button>
                </ModalFooter>
            </Modal>
        );
    }

}

export default BulkOrder;
